import React, { useEffect, useContext, useState } from 'react'
import { AuthContext } from '@/layout/Auth'
import { Message } from '@cx/ui'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { TextField, MenuItem, Stack, Alert, Box, Button } from '@mui/material'
import { createApiToken, getPermissions } from '@/api'
import { PermissionInfo } from '@/types'
import { Controller, useForm } from 'react-hook-form'
import Modal2 from '@/components/Modal2'
import EditPermissionModal from './EditPermissionModal'

interface APITokenModalProps {
  open: boolean
  onCancel: () => void
  onOk: () => void
}

const APITokenModal: React.FC<APITokenModalProps> = ({ open, onCancel, onOk }) => {
  const { authInfo } = useContext(AuthContext)
  const [res, setRes] = useState<{ token: string }>({ token: '' })
  const [copyModalVisible, setCopyModalVisible] = useState(false)
  const [visible, setVisible] = useState(false)
  const [list, setList] = useState<PermissionInfo[]>([])
  const orgInfo = authInfo?.orgInfo
  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    reset,
  } = useForm({
    defaultValues: {
      name: '',
      perms: [] as number[],
    },
  })

  const onSubmit = handleSubmit((data) => {
    const params = {
      org_id: orgInfo?.id!,
      ...data,
    }
    createApiToken(params).then((res) => {
      setRes(res)
      setCopyModalVisible(true)
      Message.success('生成API Token成功')
      onOk()
    })
  })

  useEffect(() => {
    if (open) {
      getPermissions({ id: orgInfo?.id!, query: {} }).then((res) => {
        setList(res.data || [])
      })
    } else {
      reset()
    }
  }, [open])

  return (
    <Modal2 title='生成 API Token' width={625} open={open} onCancel={onCancel} onOk={onSubmit}>
      <Modal2
        closable={false}
        width={625}
        open={copyModalVisible}
        title='复制API Token'
        footer={
          <Stack alignItems='flex-end' sx={{ px: 3, pb: 2 }}>
            <CopyToClipboard text={res.token}>
              <Button
                variant='contained'
                size='small'
                onClick={() => {
                  setCopyModalVisible(false)
                  onCancel()
                }}
              >
                复制并关闭
              </Button>
            </CopyToClipboard>
          </Stack>
        }
      >
        <Stack gap={3}>
          <Alert severity='info'>本页面关闭后，百川云平台将不再存储和显示 API Token，请妥善保存。</Alert>
          <Box sx={{ backgroundColor: '#eee', p: 3, borderRadius: 1, fontWeight: 600 }}>{res.token}</Box>
        </Stack>
      </Modal2>
      <EditPermissionModal
        open={visible}
        onCancel={() => {
          setVisible(false)
        }}
        onOk={(perms) => {
          setValue('perms', perms)
        }}
      />

      <Stack gap={3}>
        <Controller
          rules={{ required: '请输入备注' }}
          name='name'
          control={control}
          render={({ field }) => (
            <TextField
              required
              {...field}
              variant='outlined'
              placeholder='请输入备注'
              label='备注'
              fullWidth
              error={Boolean(errors.name)}
              helperText={errors.name?.message as string}
              size='small'
              autoComplete='off'
            />
          )}
        />
        <Controller
          rules={{ required: '请选择权限' }}
          name='perms'
          control={control}
          render={({ field }) => (
            <TextField
              required
              {...field}
              select
              SelectProps={{
                multiple: true,
                open: false,
                onOpen: () => {
                  setVisible(true)
                },
                displayEmpty: true,
              }}
              variant='outlined'
              placeholder='请选择权限'
              label='权限'
              fullWidth
              error={Boolean(errors.perms)}
              helperText={errors.perms?.message as string}
              size='small'
              autoComplete='off'
            >
              {list.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </Stack>
    </Modal2>
  )
}

export default APITokenModal
